import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Loader2, MapPin, CreditCard, Truck, ArrowRight, ArrowLeft, CheckCircle2, FileDown } from "lucide-react";
import { api, fmt, fmtDate, fmtDay, apiError, fmtPerUnit, fmtQty, downloadInvoicePdf, openRazorpayCheckout } from "@/lib/api";
import { StatusChip } from "@/pages/OrdersPage";
import { ReorderButton } from "@/components/Reorder";

const TIMELINE = [
  ["confirmed", "Order Confirmed"],
  ["payment_confirmed", "Payment Confirmed"],
  ["packed", "Packed"],
  ["dispatched", "Dispatched"],
  ["in_transit", "In Transit"],
  ["out_for_delivery", "Out for Delivery"],
  ["delivered", "Delivered"],
];

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [paying, setPaying] = useState(false);
  const confirmed = searchParams.get("confirmed") === "1";
  const access = searchParams.get("access") || "";

  useEffect(() => {
    setOrder(null);
    setError("");
    api.get(`/orders/${orderId}`, { params: access ? { access } : {} })
      .then(({ data }) => setOrder(data))
      .catch((e) => setError(apiError(e, "Order not found")));
  }, [orderId, access]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center" data-testid="order-error">
        <h1 className="font-display text-3xl font-semibold tracking-tight">{error}</h1>
        <p className="max-w-sm text-sm text-3">You may need to sign in with the account that placed this order.</p>
        <button onClick={() => navigate("/orders")} className="mt-2 rounded-full bg-amber-500 px-7 py-3.5 text-sm font-bold text-black hover:bg-amber-400" data-testid="order-error-back">
          Go to Orders
        </button>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen pb-24 pt-28" data-testid="order-loading">
        <div className="mx-auto max-w-5xl animate-pulse px-6 lg:px-12">
          <div className="h-3 w-24 rounded bg-hover" />
          <div className="mt-8 flex items-end justify-between">
            <div className="space-y-2">
              <div className="h-3 w-16 rounded bg-hover" />
              <div className="h-9 w-48 rounded-lg bg-hover" />
              <div className="h-3 w-32 rounded bg-hover" />
            </div>
            <div className="h-7 w-28 rounded-full bg-hover" />
          </div>
          <div className="mt-10 grid gap-6 lg:grid-cols-5">
            <div className="space-y-6 lg:col-span-3">
              <div className="rounded-2xl border border-line bg-surface p-6 shadow-card">
                <div className="h-5 w-20 rounded bg-hover" />
                <div className="mt-5 space-y-4">
                  {[1, 2].map((i) => (
                    <div key={i} className="flex items-center gap-4">
                      <div className="h-16 w-16 rounded-xl bg-hover" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-2/3 rounded bg-hover" />
                        <div className="h-3 w-1/3 rounded bg-hover" />
                      </div>
                      <div className="h-4 w-16 rounded bg-hover" />
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border border-line bg-surface p-6 shadow-card">
                <div className="h-5 w-32 rounded bg-hover" />
                <div className="mt-6 space-y-5">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-4">
                      <div className="h-7 w-7 rounded-full bg-hover" />
                      <div className="h-4 w-40 rounded bg-hover" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="space-y-6 lg:col-span-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="rounded-2xl border border-line bg-surface p-6 shadow-card">
                  <div className="h-5 w-24 rounded bg-hover" />
                  <div className="mt-4 space-y-2">
                    <div className="h-3 w-full rounded bg-hover" />
                    <div className="h-3 w-2/3 rounded bg-hover" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const statusIndex = TIMELINE.findIndex(([s]) => s === order.status);
  const fulfilledStatuses = new Set(order.fulfillment.map((f) => f.status));

  const retryPayment = async () => {
    setPaying(true);
    try {
      const { data: cfg } = await api.get("/payments/razorpay/public-config");
      if (!cfg.enabled) throw new Error("Online payments are unavailable right now");
      const rp = await openRazorpayCheckout({
        keyId: cfg.key_id,
        orderId: order.payment.razorpay_order_id,
        amountPaise: Math.round(order.total * 100),
        prefill: { name: order.contact.name, email: order.contact.email, contact: order.contact.mobile },
        description: `PAQVERO Order ${order.order_number}`,
      });
      await api.post("/payments/razorpay/verify", {
        order_id: order.id,
        access,
        razorpay_order_id: rp.razorpay_order_id,
        razorpay_payment_id: rp.razorpay_payment_id,
        razorpay_signature: rp.razorpay_signature,
      });
      toast.success("Payment received — order confirmed");
      const { data } = await api.get(`/orders/${order.id}`, { params: access ? { access } : {} });
      setOrder(data);
    } catch (e) {
      if (e?.reason !== "cancelled") toast.error(apiError(e, "Payment couldn't be completed"));
    } finally {
      setPaying(false);
    }
  };

  const downloadInvoice = async () => {
    setDownloading(true);
    try {
      await downloadInvoicePdf(order.id, access);
    } catch (e) {
      toast.error(apiError(e, "Couldn't download the invoice"));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen pb-24 pt-28" data-testid="order-detail-page">
      <div className="mx-auto max-w-5xl px-6 lg:px-12">
        <button
          data-testid="order-back-btn"
          onClick={() => navigate("/orders")}
          className="mb-6 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-3 transition-colors hover:text-amber-400"
        >
          <ArrowLeft className="h-4 w-4" /> All Orders
        </button>

        {confirmed && (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mb-8 flex items-center gap-4 rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.06] p-6"
            data-testid="order-confirmed-banner"
          >
            <CheckCircle2 className="h-9 w-9 shrink-0 text-emerald-400" />
            <div>
              <h2 className="font-display text-2xl font-semibold tracking-tight text-emerald-400">PAQVERO ORDER CONFIRMED</h2>
              <p className="mt-0.5 text-sm text-2">
                {order.credit
                  ? `Payment method: PAQVERO Credit · Amount due ${fmt(order.credit.amount_due)} by ${fmtDate(order.credit.due_date)}. Your order has been confirmed — payment is due according to your PAQVERO Credit terms.`
                  : `Your PAQVERO order has been confirmed. A confirmation is on its way to ${order.contact.email}.`}
              </p>
            </div>
          </motion.div>
        )}

        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-3">Order</p>
            <h1 className="font-display mt-1 text-4xl font-semibold tracking-tighter" data-testid="order-number">
              #{order.order_number}
            </h1>
            <p className="mt-1 text-sm text-3">Placed {fmtDate(order.created_at)}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <StatusChip status={order.status} />
            <ReorderButton orderId={order.id} orderNumber={order.order_number} status={order.status} prominent />
          </div>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-5">
          <div className="space-y-6 lg:col-span-3">
            <section className="rounded-2xl border border-line bg-surface p-6 shadow-card" data-testid="order-items">
              <h3 className="font-display mb-5 text-lg font-medium tracking-tight text-1">Items</h3>
              <div className="space-y-4">
                {order.items.map((item) => (
                  <div key={item.sku} className="flex items-center gap-4" data-testid={`order-item-${item.sku}`}>
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-line bg-white p-1.5">
                      <img src={item.image} alt="" className="max-h-full max-w-full object-contain" loading="lazy" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-1">{item.name}</p>
                      <p className="text-xs text-3">{item.variant_label} · SKU {item.sku}</p>
                      <p className="mt-0.5 text-xs text-3">{fmtQty(item.qty, item.unit)} × {fmtPerUnit(item.unit_price, item.unit)}</p>
                    </div>
                    <span className="text-sm font-bold text-1">{fmt(item.line_total)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-5 space-y-2 border-t border-line pt-4 text-sm">
                <div className="flex justify-between"><span className="text-2">Subtotal</span><span>{fmt(order.subtotal)}</span></div>
                {order.discount_total > 0 && (
                  <div className="flex justify-between"><span className="text-2">Discount {order.coupon_code && `(${order.coupon_code})`}</span><span className="text-emerald-400">−{fmt(order.discount_total)}</span></div>
                )}
                <div className="flex justify-between"><span className="text-2">Shipping ({order.shipping_method}{order.delivery_zone?.zone_label ? ` · ${order.delivery_zone.zone_label}` : ""})</span><span>{order.shipping_fee === 0 ? "FREE" : fmt(order.shipping_fee)}</span></div>
                {order.delivery_zone?.estimate_text && (
                  <div className="flex justify-between" data-testid="order-delivery-estimate"><span className="text-2">Estimated delivery</span><span>{order.delivery_zone.estimate_text}</span></div>
                )}
                {order.gst_enabled !== false && (
                  <div className="flex justify-between" data-testid="order-tax-row"><span className="text-2">GST</span><span>{fmt(order.tax)}</span></div>
                )}
                <div className="flex justify-between border-t border-line pt-2.5">
                  <span className="font-bold">Total</span>
                  <span className="font-display text-xl font-semibold text-amber-500" data-testid="order-total">{fmt(order.total)}</span>
                </div>
              </div>
            </section>

            <section className="rounded-2xl border border-line bg-surface p-6 shadow-card" data-testid="order-timeline">
              <h3 className="font-display mb-6 text-lg font-medium tracking-tight text-1">Order Timeline</h3>
              <ol className="space-y-0">
                {TIMELINE.map(([key, label], i) => {
                  const done = fulfilledStatuses.has(key) || i < statusIndex;
                  const current = i === statusIndex && order.status !== "delivered";
                  const entry = order.fulfillment.find((f) => f.status === key);
                  return (
                    <li key={key} className="relative flex gap-4 pb-7 last:pb-0" data-testid={`timeline-${key}`}>
                      {i < TIMELINE.length - 1 && (
                        <span className={`absolute left-[13px] top-7 h-full w-px ${done ? "bg-emerald-500/40" : "bg-hover"}`} />
                      )}
                      <span
                        className={`z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 ${
                          done
                            ? "border-emerald-500 bg-emerald-500 text-black"
                            : current
                              ? "border-amber-500 bg-surface text-amber-400"
                              : "border-strong bg-surface text-3"
                        }`}
                      >
                        {done ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : <span className={`h-1.5 w-1.5 rounded-full ${current ? "animate-blink bg-amber-400" : "bg-neutral-700"}`} />}
                      </span>
                      <div className="pt-0.5">
                        <p className={`text-sm font-bold ${done || current ? "text-1" : "text-3"}`}>
                          {key === "payment_confirmed" && order.credit ? "Order Placed on PAQVERO Credit" : label}
                        </p>
                        {entry?.note && <p className="mt-0.5 text-xs text-3">{entry.note}</p>}
                        {entry && <p className="mt-0.5 font-mono text-[11px] text-3">{fmtDate(entry.at)}</p>}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </section>
          </div>

          <div className="space-y-6 lg:col-span-2">
            <section className="rounded-2xl border border-amber-500/20 bg-amber-500/[0.04] p-6 shadow-card" data-testid="order-shipment">
              <div className="flex items-center gap-2.5">
                <Truck className="h-5 w-5 text-amber-500" />
                <h3 className="font-display text-lg font-medium tracking-tight text-1">Delivery</h3>
              </div>
              {order.tracking_id ? (
                <>
                  <p className="mt-4 text-[11px] font-bold uppercase tracking-[0.18em] text-3">Tracking ID</p>
                  <p className="font-mono text-lg font-bold tracking-wider text-amber-400" data-testid="order-tracking-id">{order.tracking_id}</p>
                  <p className="mt-1 text-xs text-3">
                    {order.shipment?.status} · {order.shipment?.current_location}
                  </p>
                  <p className="mt-1 text-xs text-3">
                    Estimated delivery: <span className="font-semibold text-2">{fmtDay(order.estimated_delivery.from)} – {fmtDay(order.estimated_delivery.to)}</span>
                  </p>
                  <div className="mt-4 space-y-1.5">
                    {(order.shipment?.checkpoints || []).filter((c) => c.state !== "upcoming").slice().reverse().slice(0, 4).map((c) => (
                      <div key={c.stage} className="flex items-center gap-2.5 text-xs">
                        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${c.state === "current" ? "animate-blink bg-amber-400" : "bg-emerald-500"}`} />
                        <span className="text-2">{c.label}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    data-testid="order-track-shipment-btn"
                    onClick={() => navigate(`/track?track=${order.tracking_id}`)}
                    className="group mt-5 flex w-full items-center justify-center gap-2 rounded-xl border border-amber-500/50 py-3 text-sm font-bold text-amber-400 transition-all hover:bg-amber-500/10"
                  >
                    Track Order
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </button>
                </>
              ) : (
                <p className="mt-4 text-sm text-3">A tracking ID will be assigned when your order is dispatched.</p>
              )}
            </section>

            <section className="rounded-2xl border border-line bg-surface p-6 shadow-card" data-testid="order-address">
              <div className="flex items-center gap-2.5">
                <MapPin className="h-5 w-5 text-amber-500" />
                <h3 className="font-display text-lg font-medium tracking-tight text-1">Delivery Address</h3>
              </div>
              <div className="mt-4 text-sm leading-relaxed text-2">
                <p className="font-bold text-1">{order.address.full_name}</p>
                <p>{order.address.line1}{order.address.line2 ? `, ${order.address.line2}` : ""}</p>
                <p>{order.address.city}, {order.address.state} — {order.address.pin}</p>
                <p>{order.address.country} · {order.address.mobile}</p>
              </div>
            </section>

            <section className="rounded-2xl border border-line bg-surface p-6 shadow-card" data-testid="order-payment">
              <div className="flex items-center gap-2.5">
                <CreditCard className="h-5 w-5 text-amber-500" />
                <h3 className="font-display text-lg font-medium tracking-tight text-1">Payment</h3>
              </div>
              {order.credit ? (
                <div className="mt-4 space-y-1.5 text-sm" data-testid="order-credit-block">
                  <p className="font-bold text-1">PAQVERO Credit</p>
                  <div className="flex justify-between"><span className="text-3">Amount</span><span className="text-1">{fmt(order.credit.amount_due)}</span></div>
                  <div className="flex justify-between"><span className="text-3">Paid</span><span className="text-emerald-400">{fmt(order.credit.paid)}</span></div>
                  <div className="flex justify-between"><span className="text-3">Outstanding</span><span className="text-1">{fmt(order.credit.outstanding)}</span></div>
                  <div className="flex justify-between"><span className="text-3">Due</span><span className="text-1">{fmtDate(order.credit.due_date)}</span></div>
                  <p className={`pt-1 text-xs font-bold uppercase tracking-wider ${
                    order.credit.status === "PAID" ? "text-emerald-400" : order.credit.status === "OVERDUE" ? "text-red-400" : "text-amber-400"
                  }`} data-testid="order-credit-status">
                    {order.credit.status === "PARTIAL" ? "Partially paid" : order.credit.status === "OPEN" ? "Open" : order.credit.status}
                    {order.credit.status === "OVERDUE" && order.credit.days_overdue > 0 && ` · ${order.credit.days_overdue} days late`}
                  </p>
                </div>
              ) : (
                <div className="mt-4 space-y-1.5 text-sm">
                  <p className="text-2">{{ upi: "UPI", card: "Card", netbanking: "Net Banking", wallet: "Wallet", cod: "Cash on Delivery" }[order.payment.method] || order.payment.method}</p>
                  <p className={`text-xs font-bold uppercase tracking-wider ${
                    order.payment.status === "paid" ? "text-emerald-400" : order.payment.status === "failed" ? "text-red-400" : "text-amber-400"
                  }`} data-testid="order-payment-status">
                    {{ paid: "Paid", cod_pending: "Payment on delivery", pending: "Payment pending", failed: "Payment failed" }[order.payment.status] || order.payment.status}
                  </p>
                  {order.payment.payment_id && <p className="font-mono text-[11px] text-3">{order.payment.payment_id}</p>}
                  {order.payment.provider === "razorpay" && ["pending", "failed"].includes(order.payment.status) && (
                    <button
                      data-testid="order-pay-now-btn"
                      onClick={retryPayment}
                      disabled={paying}
                      className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 py-3 text-sm font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-60"
                    >
                      {paying && <Loader2 className="h-4 w-4 animate-spin" />}
                      Pay {fmt(order.total)} Securely
                    </button>
                  )}
                </div>
              )}
            </section>

            <button
              data-testid="order-download-invoice-btn"
              onClick={downloadInvoice}
              disabled={downloading}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-strong py-4 text-sm font-bold text-1 transition-all hover:border-amber-500/60 hover:text-amber-400 disabled:opacity-60"
            >
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
              {!["cod", "packo_credit"].includes(order.payment?.method) && order.payment?.status !== "paid"
                ? "Download Proforma Invoice"
                : "Download Invoice"}
            </button>

            <button
              data-testid="order-continue-shopping"
              onClick={() => navigate("/shop")}
              className="w-full rounded-xl bg-amber-500 py-4 text-sm font-bold text-black transition-all hover:bg-amber-400"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
